import { useCallback, useState } from 'react';
import type { TreeOrientation } from './TreeChart';

const STORAGE_PREFIX = 'rsys.tree-orientation.';

/**
 * Which way a chart is drawn, remembered per chart.
 *
 * Persisted, unlike Show deleted. The two are different kinds of state and the distinction
 * is the point: Show deleted changes *which records* are on screen, so carrying it across
 * sessions is how a deleted record gets treated as live. Orientation changes only how the
 * same records are arranged, so remembering it is a convenience with nothing to get wrong —
 * the same reasoning the index shell persists its table/list choice under.
 *
 * Every read and write is guarded: `localStorage` throws outright in a browser set to block
 * site data, and a chart must still draw when the preference cannot be read.
 */
export function useTreeOrientation(
  key: string,
  fallback: TreeOrientation = 'horizontal',
): [TreeOrientation, (next: TreeOrientation) => void] {
  const [orientation, setOrientation] = useState<TreeOrientation>(() => {
    try {
      const stored = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
      return stored === 'vertical' || stored === 'horizontal' ? stored : fallback;
    } catch {
      return fallback;
    }
  });

  const choose = useCallback(
    (next: TreeOrientation) => {
      setOrientation(next);

      try {
        localStorage.setItem(`${STORAGE_PREFIX}${key}`, next);
      } catch {
        // The chart is already drawn the new way; only the memory of it is lost.
      }
    },
    [key],
  );

  return [orientation, choose];
}
